'use client'

import { UserRound } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

type TeamLeaderContact = {
  name: string
  email: string
  phone: string | null
  avatarUrl?: string | null
  avatarAlt?: string | null
  profileImageUrl?: string | null
  bio?: string | null
}

type EventTeamLeaderContactsProps = {
  title: string
  teamLeaders: TeamLeaderContact[]
}

function getInitials(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return ''
  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
}

function ContactAvatar({
  contact,
  size = 'sm',
}: {
  contact: TeamLeaderContact
  size?: 'sm' | 'lg'
}) {
  const initials = getInitials(contact.name)
  const sizeClass = size === 'lg' ? 'size-32' : 'size-12'
  const initialClass = size === 'lg' ? 'text-3xl font-semibold' : 'text-sm font-semibold'
  const fallbackIconClass = size === 'lg' ? 'size-14' : 'size-6'
  const imageUrl =
    size === 'lg' ? contact.profileImageUrl ?? contact.avatarUrl : contact.avatarUrl

  return (
    <div
      className={`flex ${sizeClass} shrink-0 items-center justify-center overflow-hidden rounded-full border border-black bg-neutral-100 text-neutral-700`}
    >
      {imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageUrl}
          alt={contact.avatarAlt ?? `${contact.name} profile image`}
          className="size-full object-cover"
        />
      ) : initials ? (
        <span className={initialClass}>{initials}</span>
      ) : (
        <UserRound className={fallbackIconClass} aria-hidden="true" />
      )}
    </div>
  )
}

function ContactRowBody({ contact }: { contact: TeamLeaderContact }) {
  return (
    <>
      <ContactAvatar contact={contact} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{contact.name}</p>
        <p>{contact.email}</p>
        <p>{contact.phone || 'Phone not available'}</p>
      </div>
    </>
  )
}

function ContactBioModal({ contact }: { contact: TeamLeaderContact }) {
  return (
    <DialogContent className="flex max-h-[90vh] w-[min(94vw,40rem)] flex-col overflow-hidden p-0 sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{contact.name}</DialogTitle>
        <DialogDescription>{contact.email}</DialogDescription>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <ContactAvatar contact={contact} size="lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-center sm:text-left">
            <p className="text-lg font-semibold">{contact.name}</p>
            <p className="text-sm text-muted-foreground">{contact.email}</p>
            <p className="text-sm text-muted-foreground">
              {contact.phone || 'Phone not available'}
            </p>
          </div>
        </div>

        {contact.bio ? (
          <p className="mt-6 whitespace-pre-wrap text-sm leading-relaxed">{contact.bio}</p>
        ) : null}
      </div>
    </DialogContent>
  )
}

export function EventTeamLeaderContacts({ title, teamLeaders }: EventTeamLeaderContactsProps) {
  if (teamLeaders.length === 0) return null

  return (
    <section>
      <div className="border-b border-black bg-neutral-100 p-4 py-1 font-bold">{title}</div>
      <div className="p-4 md:px-6 md:py-4">
        <ul className="divide-y divide-black">
          {teamLeaders.map((leader) => {
            const hasModalContent = Boolean(leader.bio || leader.profileImageUrl || leader.avatarUrl)

            if (!hasModalContent) {
              return (
                <li
                  key={`${leader.email}-${leader.name}`}
                  className="flex items-center gap-3 py-3 first:pt-0 last:pb-0 md:py-2"
                >
                  <ContactRowBody contact={leader} />
                </li>
              )
            }

            return (
              <li
                key={`${leader.email}-${leader.name}`}
                className="py-3 first:pt-0 last:pb-0 md:py-2"
              >
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 text-left transition-colors hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ContactRowBody contact={leader} />
                    </button>
                  </DialogTrigger>
                  <ContactBioModal contact={leader} />
                </Dialog>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
